import { useEffect, useMemo, useState } from 'react';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore } from '@/auth/authStore';
import { useUserStore } from '@/stores/userStore';
import { authApi, type CosmeticSlot } from '@/auth/authApi';
import { CosmeticPreview } from '@/shop/CosmeticPreview';
import {
  shopApi,
  ShopApiError,
  RARITY_COLOR,
  SLOT_LABEL,
  SLOT_ORDER,
  type CosmeticItem,
  type InventoryResponse,
} from '@/shop/shopApi';
import { useSkillsStore } from '@/stores/skillsStore';
import { SkillIcon } from '@/art';
import { CharactersBagSection } from '@/characters/CharactersBagSection';
import { HoldSkinsBagSection } from '@/holdskins/HoldSkinsBagSection';
import { BackButton } from '@/components/BackButton';
import { useT } from '@/i18n/useI18n';

export function InventoryScreen() {
  const t = useT();
  const go = useUIStore((s) => s.go);
  const token = useAuthStore((s) => s.token);
  const setUser = useAuthStore((s) => s.setUser);
  const setProfile = useUserStore((s) => s.setProfile);

  const [data, setData] = useState<InventoryResponse | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ text: string; kind: 'ok' | 'err' } | null>(null);
  const [tab, setTab] = useState<'heroes' | 'holds' | 'skills' | 'frames' | 'finishers' | 'backgrounds'>('skills');

  // Skill consumables — live count + meta (shop section will refetch on
  // buy, this keeps the bag in sync).
  const skills = useSkillsStore((s) => s.items);
  const refreshSkills = useSkillsStore((s) => s.refresh);

  const load = async () => {
    try {
      const inv = await shopApi.inventory();
      setData(inv);
    } catch (e) {
      setToast({ text: String(e), kind: 'err' });
    }
  };

  useEffect(() => {
    void load();
    void refreshSkills();
  }, [refreshSkills]);

  const refreshAuth = () => {
    if (!token) return;
    authApi
      .me(token)
      .then(({ user }) => {
        setUser(user);
        // Mirror the server-resolved frame into userStore so the home / match
        // avatars update immediately without needing a reload.
        setProfile({ frame: user.frame ?? null });
      })
      .catch(() => {});
  };

  const equip = async (item: CosmeticItem) => {
    setBusyId(item.id);
    try {
      await shopApi.equip(item.id);
      setData((d) =>
        d ? { ...d, equipped: { ...d.equipped, [item.slot]: item.id } } : d,
      );
      refreshAuth();
      setToast({ text: t('inv.equipped_toast', { name: item.name }), kind: 'ok' });
    } catch (e) {
      if (e instanceof ShopApiError) setToast({ text: e.message || e.code, kind: 'err' });
      else setToast({ text: t('common.network_error'), kind: 'err' });
    } finally {
      setBusyId(null);
      window.setTimeout(() => setToast(null), 2200);
    }
  };

  const unequip = async (slot: CosmeticSlot) => {
    setBusyId(slot);
    try {
      await shopApi.unequip(slot);
      setData((d) => (d ? { ...d, equipped: { ...d.equipped, [slot]: null } } : d));
      refreshAuth();
      setToast({ text: t('inv.unequipped_toast', { slot: SLOT_LABEL[slot] }), kind: 'ok' });
    } catch (e) {
      if (e instanceof ShopApiError) setToast({ text: e.message || e.code, kind: 'err' });
      else setToast({ text: t('common.network_error'), kind: 'err' });
    } finally {
      setBusyId(null);
      window.setTimeout(() => setToast(null), 2200);
    }
  };

  const grouped = useMemo(() => {
    if (!data) return null;
    const m = new Map<CosmeticSlot, CosmeticItem[]>();
    for (const it of data.owned) {
      (m.get(it.slot) ?? m.set(it.slot, []).get(it.slot)!).push(it);
    }
    return m;
  }, [data]);

  return (
    <div className="screen">
      <div className="hex-bg" />

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 18px 8px',
        }}
      >
        <BackButton onClick={() => go('home')} />
        <button
          onClick={() => go('shop')}
          className="btn btn-ghost"
          style={{
            fontSize: 10,
            padding: '6px 12px',
            color: 'var(--c-yellow)',
            borderColor: 'rgba(244,199,36,0.3)',
          }}
        >
          + {t('shop.title')}
        </button>
      </div>

      <div style={{ padding: '0 18px 6px' }}>
        <div className="display" style={{ fontSize: 26 }}>{t('inv.title')}</div>
      </div>

      {/* Category tabs — mirror the shop so the bag isn't one long scroll. */}
      <div className="tab-strip" style={{ display: 'flex', gap: 8, padding: '0 18px 8px' }}>
        {([
          { id: 'skills', icon: '⚡', label: t('shop.cat_skills'), accent: 'var(--c-cyan)' },
          { id: 'heroes', icon: '🧗', label: t('shop.cat_heroes'), accent: 'var(--c-purple)' },
          { id: 'holds', icon: '🪨', label: t('shop.cat_holds'), accent: 'var(--c-red)' },
          { id: 'finishers', icon: '💥', label: t('shop.cat_finishers'), accent: 'var(--c-purple)' },
          { id: 'frames', icon: '🖼️', label: t('shop.cat_frames'), accent: 'var(--c-yellow)' },
          { id: 'backgrounds', icon: '🌌', label: t('shop.cat_backgrounds'), accent: 'var(--c-cyan)' },
        ] as const).map((c) => {
          const on = tab === c.id;
          return (
            <button
              key={c.id}
              onClick={() => setTab(c.id)}
              style={{
                flexShrink: 0, minWidth: 70, padding: '7px 12px', borderRadius: 10, cursor: 'pointer',
                scrollSnapAlign: 'center',
                border: `1px solid ${on ? c.accent : 'var(--hairline)'}`,
                background: on ? `${c.accent}1f` : 'rgba(0,0,0,0.25)',
                color: on ? c.accent : 'var(--ink-dim)',
                fontFamily: 'var(--f-display)', fontSize: 9, letterSpacing: '0.04em',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, whiteSpace: 'nowrap',
              }}
            >
              <span style={{ fontSize: 16 }}>{c.icon}</span>
              {c.label}
            </button>
          );
        })}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '6px 18px 22px' }}>
        {tab === 'heroes' && <CharactersBagSection />}
        {tab === 'holds' && <HoldSkinsBagSection />}
        {tab === 'skills' && <SkillsBagSection items={skills} onTapBuy={() => go('shop')} />}

        {(tab === 'frames' || tab === 'finishers' || tab === 'backgrounds') && (!grouped ? (
          <div style={{ color: 'var(--ink-muted)', fontSize: 12, padding: 20, textAlign: 'center' }}>
            {t('common.loading')}
          </div>
        ) : grouped.size === 0 ? (
          <div style={{ color: 'var(--ink-muted)', fontSize: 12, padding: 20, textAlign: 'center' }}>
            {t('inv.empty_all')}
          </div>
        ) : (
          (tab === 'finishers'
            ? (['endgameFx'] as CosmeticSlot[])
            : tab === 'backgrounds'
              ? (['matchBackground'] as CosmeticSlot[])
              : SLOT_ORDER
          ).map((slot) => {
            const items = grouped.get(slot);
            if (!items || items.length === 0) return null;
            const equippedId = data?.equipped[slot] ?? null;
            return (
              <div key={slot} style={{ marginBottom: 14 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 2px 6px',
                  }}
                >
                  <span className="tag">{SLOT_LABEL[slot].toUpperCase()}</span>
                  {equippedId && (
                    <button
                      onClick={() => unequip(slot)}
                      disabled={busyId === slot}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--c-red)',
                        fontFamily: 'var(--f-display)',
                        fontSize: 9,
                        letterSpacing: '0.08em',
                        cursor: 'pointer',
                      }}
                    >
                      {t('inv.unequip')}
                    </button>
                  )}
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                    gap: 8,
                  }}
                >
                  {items.map((it) => {
                    const equipped = equippedId === it.id;
                    return (
                      <div
                        key={it.id}
                        className="card"
                        style={{
                          padding: 10,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 6,
                          borderColor: equipped
                            ? 'rgba(34,211,238,0.6)'
                            : `${RARITY_COLOR[it.rarity]}55`,
                          background: equipped
                            ? 'rgba(34,211,238,0.06)'
                            : undefined,
                          position: 'relative',
                        }}
                      >
                        <span
                          className="tag"
                          style={{
                            position: 'absolute',
                            top: 6,
                            right: 8,
                            color: RARITY_COLOR[it.rarity],
                            fontSize: 8,
                          }}
                        >
                          {it.rarity.toUpperCase()}
                        </span>
                        <CosmeticPreview item={it} size={48} />
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            textAlign: 'center',
                            minHeight: 26,
                          }}
                        >
                          {it.name}
                        </div>
                        {(it.trialUsesLeft ?? 0) > 0 && (
                          <span
                            className="display"
                            style={{
                              fontSize: 9,
                              color: 'var(--c-cyan)',
                              padding: '3px 7px',
                              borderRadius: 6,
                              background: 'rgba(34,211,238,.12)',
                              border: '1px dashed rgba(34,211,238,.55)',
                            }}
                          >
                            📺 {t('inv.trial_left', { n: it.trialUsesLeft ?? 0 })}
                          </span>
                        )}
                        {equipped ? (
                          <span
                            className="display"
                            style={{ fontSize: 11, color: 'var(--c-cyan)', padding: '6px 0' }}
                          >
                            ✓ {t('inv.equipped')}
                          </span>
                        ) : (
                          <button
                            onClick={() => equip(it)}
                            disabled={busyId === it.id || busyId === slot}
                            className="btn btn-primary"
                            style={{ width: '100%', fontSize: 10, padding: '6px 8px' }}
                          >
                            {t('inv.equip')}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        ))}
      </div>

      {toast && (
        <div
          style={{
            position: 'absolute',
            bottom: 'calc(env(safe-area-inset-bottom, 0px) + 22px)',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '10px 16px',
            borderRadius: 10,
            background:
              toast.kind === 'ok' ? 'rgba(34,211,238,0.18)' : 'rgba(230,57,70,0.18)',
            border: `1px solid ${toast.kind === 'ok' ? 'rgba(34,211,238,0.6)' : 'rgba(230,57,70,0.6)'}`,
            color: toast.kind === 'ok' ? 'var(--c-cyan)' : 'var(--c-red)',
            fontSize: 12,
            zIndex: 30,
            backdropFilter: 'blur(6px)',
          }}
        >
          {toast.text}
        </div>
      )}
    </div>
  );
}

interface SkillBagItem {
  skill: 'disrupt' | 'shield';
  owned: number;
  unlocked: boolean;
  unlockLevel: number;
  myLevel: number;
}

function SkillsBagSection({
  items, onTapBuy,
}: {
  items: SkillBagItem[];
  onTapBuy: () => void;
}) {
  const t = useT();
  const META = {
    disrupt: { name: 'DISRUPT', color: 'var(--c-purple)', icon: <SkillIcon skill="disrupt" size={39} /> },
    shield: { name: 'SHIELD', color: 'var(--c-cyan)', icon: <SkillIcon skill="shield" size={39} /> },
  } as const;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '8px 2px 6px',
      }}>
        <span className="tag">{t('inv.skills_section')}</span>
        <span
          onClick={onTapBuy}
          style={{ fontSize: 10, color: 'var(--c-yellow)', cursor: 'pointer' }}
        >
          {t('inv.buy_more')}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {(['disrupt', 'shield'] as const).map((k) => {
          const it = items.find((s) => s.skill === k);
          const owned = it?.owned ?? 0;
          const unlocked = it?.unlocked ?? false;
          const meta = META[k];
          const empty = owned <= 0;
          return (
            <div
              key={k}
              onClick={onTapBuy}
              className="card"
              style={{
                padding: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                cursor: 'pointer',
                borderColor: unlocked ? `${meta.color}55` : 'var(--hairline)',
                opacity: unlocked ? 1 : 0.55,
                position: 'relative',
              }}
            >
              <div
                style={{
                  width: 60,
                  height: 60,
                  flexShrink: 0,
                  borderRadius: 12,
                  background: `${meta.color}1f`,
                  border: `1px solid ${meta.color}55`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {meta.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="display" style={{ fontSize: 13, color: meta.color, letterSpacing: '0.1em' }}>
                  {meta.name}
                </div>
                {!unlocked ? (
                  <div style={{ fontSize: 10, color: 'var(--c-red)', marginTop: 2 }}>
                    {t('inv.skill_locked', { level: it?.unlockLevel ?? '?', my: it?.myLevel ?? 1 })}
                  </div>
                ) : empty ? (
                  <div style={{ fontSize: 10, color: 'var(--c-red)', marginTop: 2 }}>
                    {t('inv.skill_empty')}
                  </div>
                ) : (
                  <div style={{ fontSize: 10, color: 'var(--ink-muted)', marginTop: 2 }}>
                    {t('inv.skill_ready')}
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 9, color: 'var(--ink-dim)', fontFamily: 'var(--f-display)' }}>{t('inv.owned_label')}</div>
                <div
                  className="mono"
                  style={{
                    fontSize: 20,
                    color: empty ? 'var(--c-red)' : 'var(--c-cyan)',
                    fontWeight: 700,
                  }}
                >
                  ×{owned}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
